import React, { useEffect, useState, useCallback } from 'react';
import { Row, Col, Select } from 'antd';
import { Link } from 'react-router-dom';
import {
    HomeOutlined,
    GiftOutlined,
    RocketOutlined,
    StarOutlined,
    DollarOutlined,
    TrophyOutlined,
    FireOutlined,
    CrownOutlined,
    ThunderboltOutlined,
    CheckCircleFilled,
    AppleOutlined,
    EnvironmentOutlined
} from '@ant-design/icons';
import { useScrollAnimation } from '../../common/hooks';
import { settingsService, cityService } from '../../common/services';
import { GOOGLE_PLAY_URL, APP_STORE_URL, APP_STORE_AVAILABLE } from '../../common/constants';
import './AvantagesPage.css';

// Valeurs par défaut
const DEFAULT_SETTINGS = {
    bonusPremiereRecharge: '50%',
    pointsNouveau: '2 000 pts',
    pointsAncien: '1 000 pts',
    cashbackRecharge: '3%',
    bonusHeures: '2 000 pts',
    seuilHeures: '130h',
    bonusCourses: '2 000 pts',
    seuilCourses: '55 courses',
    bonusLundi: '2 000 pts',
    montantMinLundi: '10 000 F',
    bonusMaxChallenge: '4 000 pts'
};

// Fonction pour générer les benefits avec les valeurs dynamiques
const getDriverBenefits = (settings) => [
    {
        icon: <GiftOutlined />,
        title: `${settings.bonusPremiereRecharge} Bonus Première Recharge`,
        description: `Anciens comme nouveaux chauffeurs reçoivent ${settings.bonusPremiereRecharge} en points sur votre première recharge.`,
        highlight: settings.bonusPremiereRecharge,
        tag: 'Bienvenue'
    },
    {
        icon: <RocketOutlined />,
        title: 'Nouveau Compte TIGOON + Yandex',
        description: 'Créez un nouveau compte TIGOON qui génère un nouveau compte Yandex.',
        highlight: settings.pointsNouveau,
        tag: 'Nouveau'
    },
    {
        icon: <StarOutlined />,
        title: 'Migration vers TIGOON',
        description: 'Ancien compte Yandex qui migre vers TIGOON.',
        highlight: settings.pointsAncien,
        tag: 'Migration'
    },
    {
        icon: <DollarOutlined />,
        title: 'Cashback sur Recharges',
        description: 'À chaque rechargement effectué par un compte Yandex lié à un compte TIGOON actif.',
        highlight: settings.cashbackRecharge,
        tag: 'Récurrent'
    },
    {
        icon: <TrophyOutlined />,
        title: `Bonus ${settings.seuilCourses}`,
        description: `Nouveaux comptes TIGOON ayant effectué ${settings.seuilCourses} en 1 semaine.`,
        highlight: settings.bonusMaxChallenge,
        tag: 'Challenge'
    },
    {
        icon: <FireOutlined />,
        title: `${settings.seuilHeures} de Course`,
        description: `Anciens et nouveaux chauffeurs atteignant ${settings.seuilHeures} de courses dans le mois.`,
        highlight: settings.bonusHeures,
        tag: 'Mensuel'
    },
    {
        icon: <CrownOutlined />,
        title: '0% Commission Partenaire',
        description: 'Profitez de 0% de commission partenaire.',
        highlight: settings.bonusCourses,
        tag: 'Premium'
    },
    {
        icon: <ThunderboltOutlined />,
        title: 'Lundi Bonus',
        description: `Premier rechargement de ${settings.montantMinLundi} le lundi.`,
        highlight: settings.bonusLundi,
        tag: 'Hebdo'
    }
];

const AvantagesPage = () => {
    const [pageLoaded, setPageLoaded] = useState(false);
    const [settings, setSettings] = useState(DEFAULT_SETTINGS);
    const [driverBenefits, setDriverBenefits] = useState(getDriverBenefits(DEFAULT_SETTINGS));
    const [cities, setCities] = useState([]);
    const [selectedCity, setSelectedCity] = useState(null);

    // Animation hooks
    const [benefitsRef, benefitsVisible] = useScrollAnimation({ threshold: 0.1 });
    const [whyRef, whyVisible] = useScrollAnimation({ threshold: 0.1 });
    const [ctaRef, ctaVisible] = useScrollAnimation({ threshold: 0.1 });

    const fetchSettings = useCallback(async (cityCode) => {
        try {
            const response = await settingsService.getPublicSettings(cityCode);
            if (response?.success && response.data?.display) {
                setSettings(response.data.display);
                setDriverBenefits(getDriverBenefits(response.data.display));
            }
        } catch (error) {
            console.error('Error fetching settings:', error);
        }
    }, []);

    useEffect(() => {
        setTimeout(() => setPageLoaded(true), 100);

        const loadCities = async () => {
            const response = await cityService.getActiveCities();
            if (response?.success && response.data?.length > 0) {
                setCities(response.data);
                // Sélectionner Abidjan par défaut, sinon la première ville
                const defaultCity = response.data.find(c => c.code?.toUpperCase() === 'ABJ') || response.data[0];
                setSelectedCity(defaultCity.code);
                fetchSettings(defaultCity.code);
            } else {
                fetchSettings();
            }
        };
        loadCities();
    }, [fetchSettings]);

    const handleCityChange = (cityCode) => {
        setSelectedCity(cityCode);
        fetchSettings(cityCode);
    };

    return (
        <div className="avantages-page">
            {/* Hero Section - Simple Banner */}
            <section className={`page-banner ${pageLoaded ? 'banner-loaded' : ''}`}>
                <div className="banner-bg-pattern"></div>
                <div className="container">
                    <h1 className={`banner-title ${pageLoaded ? 'animate-in' : ''}`}>
                        Avantages
                    </h1>
                    <p className={`banner-description ${pageLoaded ? 'animate-in' : ''}`}>
                        Découvrez tous les avantages exclusifs réservés à nos chauffeurs partenaires
                    </p>
                    <nav className={`breadcrumb ${pageLoaded ? 'animate-in' : ''}`}>
                        <Link to="/"><HomeOutlined /> Accueil</Link>
                        <span className="separator">/</span>
                        <span className="current">Avantages</span>
                    </nav>
                </div>
            </section>

            {/* Benefits Grid Section */}
            <section
                ref={benefitsRef}
                className={`benefits-grid-section ${benefitsVisible ? 'section-visible' : ''}`}
            >
                <div className="container">
                    <div className={`section-header ${benefitsVisible ? 'animate-fade-up is-visible' : 'animate-fade-up'}`}>
                        <span className="section-badge">Bonus</span>
                        <h2><span className="text-gold">Gagnez plus</span> avec TIGOON Connect</h2>
                        <p>Des bonus et récompenses pour maximiser vos revenus</p>
                        {cities.length > 0 && (
                            <div className="city-selector">
                                <EnvironmentOutlined className="city-selector-icon" />
                                <Select
                                    value={selectedCity}
                                    onChange={handleCityChange}
                                    style={{ width: 180 }}
                                    suffixIcon={null}
                                >
                                    {cities.map(city => (
                                        <Select.Option key={city.code} value={city.code}>
                                            {city.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </div>
                        )}
                    </div>
                    <Row gutter={[24, 24]}>
                        {driverBenefits.map((benefit, index) => (
                            <Col xs={24} sm={12} md={6} key={index}>
                                <div
                                    className={`benefit-card ${benefitsVisible ? 'animate-scale is-visible' : 'animate-scale'}`}
                                    style={{ transitionDelay: `${index * 0.1}s` }}
                                >
                                    <div className="benefit-tag">{benefit.tag}</div>
                                    <div className="benefit-icon">{benefit.icon}</div>
                                    <div className="benefit-highlight">{benefit.highlight}</div>
                                    <h3 className="benefit-title">{benefit.title}</h3>
                                    <p className="benefit-description">{benefit.description}</p>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </div>
            </section>

            {/* Why Choose Us Section */}
            <section
                ref={whyRef}
                className={`why-choose-section ${whyVisible ? 'section-visible' : ''}`}
            >
                <div className="container">
                    <Row gutter={[48, 48]} align="middle">
                        <Col xs={24} md={12}>
                            <div className={`why-content ${whyVisible ? 'animate-fade-right is-visible' : 'animate-fade-right'}`}>
                                <span className="section-badge">Avantages</span>
                                <h2>Pourquoi choisir <span className="text-gold">TIGOON Connect</span> ?</h2>
                                <ul className="why-list">
                                    <li>
                                        <CheckCircleFilled />
                                        <span>Des bonus généreux dès votre inscription</span>
                                    </li>
                                    <li>
                                        <CheckCircleFilled />
                                        <span>Cashback sur toutes vos recharges</span>
                                    </li>
                                    <li>
                                        <CheckCircleFilled />
                                        <span>Challenges hebdomadaires et mensuels</span>
                                    </li>
                                    <li>
                                        <CheckCircleFilled />
                                        <span>Programme de fidélité avantageux</span>
                                    </li>
                                    <li>
                                        <CheckCircleFilled />
                                        <span>Support client disponible 24/7</span>
                                    </li>
                                    <li>
                                        <CheckCircleFilled />
                                        <span>Application simple et intuitive</span>
                                    </li>
                                </ul>
                            </div>
                        </Col>
                        <Col xs={24} md={12}>
                            <div className={`stats-grid ${whyVisible ? 'animate-fade-left is-visible' : 'animate-fade-left'}`}>
                                <div className="stat-card" style={{ transitionDelay: '0.1s' }}>
                                    <span className="stat-value">{settings.bonusPremiereRecharge}</span>
                                    <span className="stat-label">Bonus 1ère recharge</span>
                                </div>
                                <div className="stat-card" style={{ transitionDelay: '0.2s' }}>
                                    <span className="stat-value">{settings.bonusMaxChallenge?.replace(' pts', '')}</span>
                                    <span className="stat-label">Points max par challenge</span>
                                </div>
                                <div className="stat-card" style={{ transitionDelay: '0.3s' }}>
                                    <span className="stat-value">{settings.cashbackRecharge}</span>
                                    <span className="stat-label">Cashback récurrent</span>
                                </div>
                                <div className="stat-card" style={{ transitionDelay: '0.4s' }}>
                                    <span className="stat-value">24/7</span>
                                    <span className="stat-label">Support disponible</span>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </div>
            </section>

            {/* CTA Section */}
            <section
                ref={ctaRef}
                className={`cta-section ${ctaVisible ? 'section-visible' : ''}`}
            >
                <div className="container">
                    <div className={`cta-content ${ctaVisible ? 'animate-scale is-visible' : 'animate-scale'}`}>
                        <h2>Prêt à profiter de ces <span className="text-gold">avantages</span> ?</h2>
                        <p>Téléchargez l'application maintenant et commencez à gagner des points</p>
                        <div className="cta-buttons">
                            <a href={GOOGLE_PLAY_URL} target="_blank" rel="noopener noreferrer" className="store-badge">
                                <img
                                    src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                                    alt="Google Play"
                                    height="50"
                                />
                            </a>
                            {APP_STORE_AVAILABLE ? (
                                <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="store-badge">
                                    <img
                                        src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg"
                                        alt="App Store"
                                        height="50"
                                    />
                                </a>
                            ) : (
                                <div className="store-badge-coming-soon">
                                    <AppleOutlined />
                                    <span>Bientôt disponible</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default AvantagesPage;
